import { useEffect, useMemo, useState } from 'react'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { Dices, Swords } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { Player } from '@/types/turn'
import { LinhaParticipante } from './LinhaParticipante'
import { ListaDaOrdem } from './ListaDaOrdem'
import { filaInicial, useOrdemDaRodada } from './useOrdemDaRodada'

export interface AbrirRodadaSheetProps {
  isOpen: boolean
  onClose: () => void
  onStartTurn: (config: { playerOrder: Player[]; timeLimit: number }) => void
  players: Player[]
  tableName?: string
}

/**
 * Tempo por vez. `0` = sem limite (rodada ativa, sem cronômetro; a troca de vez
 * fica manual). O servidor aceita esta lista — quem decide o cardápio é aqui.
 */
const TEMPOS = [
  { valor: 30, rotulo: '30s' },
  { valor: 60, rotulo: '1 min' },
  { valor: 120, rotulo: '2 min' },
  { valor: 300, rotulo: '5 min' },
  { valor: 600, rotulo: '10 min' },
  { valor: 0, rotulo: 'Sem limite' },
]

function descreverTempo(segundos: number): string {
  if (segundos === 0) return 'sem limite de tempo'
  if (segundos < 60) return `${segundos}s por vez`
  return `${segundos / 60} min por vez`
}

/**
 * A tela de abrir a rodada — reescrita mobile first (fase 4).
 *
 * Uma coluna só, de cima para baixo: tempo, os três grupos de participantes, a
 * ordem, e o rodapé fixo com a ação. A tela antiga era uma grade de cartões que
 * não cabia em 360px (300px de cartões + vão em 312px úteis: o terceiro era
 * cortado), com setas de 24px e cartões que o leitor de tela não enxergava.
 */
export function AbrirRodadaSheet({
  isOpen,
  onClose,
  onStartTurn,
  players,
  tableName,
}: AbrirRodadaSheetProps) {
  const jogadores = useMemo(() => players.filter(p => !p.isNpc), [players])
  const npcs = useMemo(() => players.filter(p => p.isNpc), [players])

  const naMesa = useMemo(() => jogadores.filter(p => p.online !== false), [jogadores])
  const foraDaMesa = useMemo(() => jogadores.filter(p => p.online === false), [jogadores])

  const ordem = useOrdemDaRodada(players)
  const [tempo, setTempo] = useState(120)

  // Reabrir a tela recomeça a escolha. Depende SÓ de `isOpen`: com `players` na
  // lista, alguém entrando na mesa apagaria a ordem já montada pelo mestre.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (isOpen) {
      ordem.definir(filaInicial(players))
      setTempo(120)
    }
  }, [isOpen])

  const porId = useMemo(() => {
    const mapa = new Map(players.map(p => [p.id, p]))
    return (id: string) => mapa.get(id)
  }, [players])

  const primeiro = ordem.fila.length > 0 ? porId(ordem.fila[0]) : undefined
  const vazia = ordem.fila.length === 0

  const confirmar = () => {
    if (vazia) return
    const playerOrder = ordem.fila.map(porId).filter(Boolean) as Player[]
    onStartTurn({ playerOrder, timeLimit: tempo })
    onClose()
  }

  const grupo = (titulo: string, gente: Player[], rodape?: string) => {
    if (gente.length === 0) return null
    return (
      <section className="space-y-2">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {titulo}
        </h3>
        <div className="space-y-1.5">
          {gente.map(p => (
            <LinhaParticipante
              key={p.id}
              participante={p}
              marcado={ordem.estaNaFila(p.id)}
              posicao={ordem.posicao(p.id)}
              onAlternar={() => ordem.alternar(p.id)}
            />
          ))}
        </div>
        {rodape && <p className="text-xs text-muted-foreground">{rodape}</p>}
      </section>
    )
  }

  return (
    <Sheet open={isOpen} onOpenChange={aberto => { if (!aberto) onClose() }}>
      <SheetContent
        side="bottom"
        className="h-[100dvh] md:h-auto md:max-h-[85vh] md:max-w-lg md:left-1/2 md:right-auto md:-translate-x-1/2 md:bottom-auto md:top-1/2 md:-translate-y-1/2 md:rounded-2xl p-0 gap-0 flex flex-col"
      >
        <SheetHeader className="px-4 py-3 border-b space-y-0.5">
          <SheetTitle className="text-base flex items-center gap-2">
            <Swords className="w-4 h-4 text-primary" />
            Abrir rodada
          </SheetTitle>
          {tableName && (
            <p className="text-xs text-muted-foreground truncate">{tableName}</p>
          )}
        </SheetHeader>

        {/* uma coluna, rolagem só na vertical */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden px-4 py-4 space-y-5">
          <section className="space-y-2">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Tempo por vez
            </h3>
            <div className="flex flex-wrap gap-2">
              {TEMPOS.map(t => (
                <button
                  key={t.valor}
                  type="button"
                  role="radio"
                  aria-checked={tempo === t.valor}
                  onClick={() => setTempo(t.valor)}
                  className={cn(
                    'min-h-[44px] px-4 rounded-xl border text-sm font-medium',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
                    'focus-visible:ring-offset-2 focus-visible:ring-offset-background',
                    'motion-reduce:transition-none',
                    tempo === t.valor
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-card border-border hover:bg-accent',
                  )}
                >
                  {t.rotulo}
                </button>
              ))}
            </div>
          </section>

          {grupo('Na mesa agora', naMesa)}
          {grupo('Fora da mesa', foraDaMesa)}
          {grupo('NPCs', npcs, 'No turno de um NPC, só o mestre escreve.')}

          <section className="space-y-2">
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Ordem da rodada
              </h3>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-11 gap-1.5 text-xs"
                disabled={ordem.fila.length < 2}
                onClick={ordem.embaralhar}
              >
                <Dices className="w-4 h-4" />
                Sortear iniciativa
              </Button>
            </div>
            {vazia ? (
              <p className="text-xs text-muted-foreground">
                Ninguém na fila ainda. Marque quem vai jogar.
              </p>
            ) : (
              <ListaDaOrdem
                fila={ordem.fila}
                participantePorId={porId}
                onMover={ordem.mover}
              />
            )}
          </section>
        </div>

        {/* rodapé fixo: a ação nunca sai da tela */}
        <div className="border-t px-4 py-3 space-y-1.5 bg-background">
          <Button
            type="button"
            className="w-full min-h-[48px] text-sm font-semibold"
            disabled={vazia}
            onClick={confirmar}
          >
            {vazia ? 'Marque quem vai jogar' : `Começar por ${primeiro?.name ?? ''}`}
          </Button>
          <p className="text-xs text-muted-foreground text-center">
            {vazia
              ? 'Ninguém da mesa está marcado.'
              : `${descreverTempo(tempo)} · ${tempo === 0
                  ? 'você troca a vez quando quiser'
                  : 'o relógio começa quando você tocar em Iniciar'}`}
          </p>
        </div>
      </SheetContent>
    </Sheet>
  )
}